import { useEffect, useState } from "react";
import { getSessionToken } from "../../hooks/useSession.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { ModalOverlay } from "../ui/Modal.js";
import { PageHeader } from "../ui/PageHeader.js";
import { DataTableSkeleton, EmptyState } from "../ui/index.js";
import { useToast } from "../../context/ToastContext.js";
import { ComposeEmailModal, type ComposeDraft } from "./inbox/ComposeEmailModal.js";
import { EmailSettingsModal } from "./inbox/EmailSettingsModal.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

const PAGE_SIZE = 25;

type InboxKind = "outbound" | "inbound";

const TABS: Array<{ kind: InboxKind; label: string; description: string }> = [
  {
    kind: "outbound",
    label: "Outbound",
    description: "Email Altura sent to agents and staff through Resend, newest first.",
  },
  {
    kind: "inbound",
    label: "Inbound",
    description: "Email Altura received at its Resend inbound addresses, newest first.",
  },
];

interface InboxEmail {
  id: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo: string[];
  subject: string;
  createdAt: string | null;
  lastEvent: string | null;
  attachmentCount: number;
  kind: InboxKind;
}

interface InboxEmailDetail extends InboxEmail {
  html: string | null;
  text: string | null;
  scheduledAt: string | null;
}

function formatSentAt(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRecipients(recipients: string[]) {
  return recipients.length > 0 ? recipients.join(", ") : "—";
}

function displaySubject(email: { subject: string }) {
  return email.subject || "(no subject)";
}

function eventLabel(lastEvent: string | null) {
  if (!lastEvent) return "Unknown";
  const spaced = lastEvent.replace(/[_-]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function eventVariant(lastEvent: string | null) {
  switch (lastEvent) {
    case "delivered":
    case "opened":
    case "clicked":
      return "success" as const;
    case "bounced":
    case "failed":
    case "complained":
      return "danger" as const;
    case "delivery_delayed":
      return "warning" as const;
    default:
      return "default" as const;
  }
}

function EmailBody({ email }: { email: InboxEmailDetail }) {
  if (email.html) {
    return (
      <iframe
        title={`Email body: ${displaySubject(email)}`}
        srcDoc={email.html}
        sandbox=""
        className="h-[60vh] w-full rounded-lg border border-border-default bg-white"
      />
    );
  }
  if (email.text) {
    return (
      <pre className="h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border-default bg-surface-overlay p-4 text-sm text-content-primary">
        {email.text}
      </pre>
    );
  }
  return (
    <p className="rounded-lg border border-dashed border-border-default px-4 py-8 text-center text-sm text-content-muted">
      This email has no stored body.
    </p>
  );
}

function escapeHtml(value: string) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function addressOf(value: string) {
  const match = value.match(/<([^>]+)>/);
  return (match ? match[1] : value).trim().toLowerCase();
}

/** A reply to the sender (inbound) or a follow-up to the recipients (outbound), quoting the original. */
function replyDraft(email: InboxEmailDetail, kind: InboxKind): ComposeDraft {
  const original = email.text?.trim() || (email.html ? email.html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "");
  const quoted = escapeHtml(original.slice(0, 4000)).replace(/\n/g, "<br>");
  const who = kind === "inbound" ? email.from : "we";
  return {
    to: kind === "inbound" ? [addressOf(email.replyTo[0] ?? email.from)] : email.to.map(addressOf),
    cc: kind === "inbound" ? email.cc.map(addressOf) : [],
    subject: /^re:/i.test(email.subject) ? email.subject : `Re: ${email.subject}`,
    html: `<p></p><blockquote><p>On ${escapeHtml(formatSentAt(email.createdAt))}, ${escapeHtml(who)} wrote:</p><p>${quoted}</p></blockquote>`,
  };
}

function EmailDetailModal({
  emailId,
  kind,
  onClose,
  onReply,
}: {
  emailId: string;
  kind: InboxKind;
  onClose: () => void;
  onReply: (draft: ComposeDraft) => void;
}) {
  const token = getSessionToken();
  const [email, setEmail] = useState<InboxEmailDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setEmail(null);
    setError(null);
    void fetch(`${API_BASE}/api/admin/inbox/${encodeURIComponent(emailId)}?kind=${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || "Unable to load this email");
        }
        return json.data as InboxEmailDetail;
      })
      .then((data) => {
        if (!cancelled) setEmail(data);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load this email");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, emailId, kind]);

  return (
    <ModalOverlay
      onClose={onClose}
      ariaLabel="Email detail"
      className="[&>div]:max-w-4xl [&>div]:max-h-[90vh] [&>div]:overflow-y-auto"
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="min-w-0 font-display text-lg font-semibold text-content-primary">
            {email ? displaySubject(email) : "Email"}
          </h2>
          <div className="flex shrink-0 gap-2">
            {email && (
              <Button size="sm" onClick={() => onReply(replyDraft(email, kind))}>
                Reply
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-lg border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm text-status-rejected-text"
          >
            {error}
          </p>
        )}

        {!email && !error && (
          <div role="status" aria-busy="true" className="h-[60vh] animate-pulse rounded-lg bg-skeleton motion-reduce:animate-none">
            <span className="sr-only">Loading email</span>
          </div>
        )}

        {email && (
          <>
            <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[6rem_1fr]">
              <dt className="text-content-muted">From</dt>
              <dd className="break-words text-content-primary">{email.from || "—"}</dd>
              <dt className="text-content-muted">To</dt>
              <dd className="break-words text-content-primary">{formatRecipients(email.to)}</dd>
              {email.replyTo.length > 0 && (
                <>
                  <dt className="text-content-muted">Reply to</dt>
                  <dd className="break-words text-content-primary">{formatRecipients(email.replyTo)}</dd>
                </>
              )}
              {email.cc.length > 0 && (
                <>
                  <dt className="text-content-muted">Cc</dt>
                  <dd className="break-words text-content-primary">{formatRecipients(email.cc)}</dd>
                </>
              )}
              {email.bcc.length > 0 && (
                <>
                  <dt className="text-content-muted">Bcc</dt>
                  <dd className="break-words text-content-primary">{formatRecipients(email.bcc)}</dd>
                </>
              )}
              <dt className="text-content-muted">{kind === "inbound" ? "Received" : "Sent"}</dt>
              <dd className="text-content-primary">{formatSentAt(email.createdAt)}</dd>
              {kind === "inbound" ? (
                <>
                  <dt className="text-content-muted">Attachments</dt>
                  <dd className="text-content-primary">{email.attachmentCount || "None"}</dd>
                </>
              ) : (
                <>
                  <dt className="text-content-muted">Status</dt>
                  <dd>
                    <Badge variant={eventVariant(email.lastEvent)}>{eventLabel(email.lastEvent)}</Badge>
                  </dd>
                </>
              )}
            </dl>
            <EmailBody email={email} />
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

export function AdminInbox() {
  const token = getSessionToken();
  const [kind, setKind] = useState<InboxKind>("outbound");
  /** Cursor stack: each entry is the `after` id a page was opened with. */
  const [cursors, setCursors] = useState<string[]>([]);
  const [emails, setEmails] = useState<InboxEmail[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [compose, setCompose] = useState<ComposeDraft | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { success } = useToast();

  const cursor = cursors[cursors.length - 1];
  const tab = TABS.find((entry) => entry.kind === kind) ?? TABS[0];
  const tableHeaders =
    kind === "inbound"
      ? ["Received", "From", "Subject", "Attachments"]
      : ["Sent", "To", "Subject", "Status"];

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setEmails(null);
    setError(null);
    const query = new URLSearchParams({ limit: String(PAGE_SIZE), kind });
    if (cursor) query.set("after", cursor);
    void fetch(`${API_BASE}/api/admin/inbox?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || "Unable to load the inbox");
        }
        return json.data as { emails: InboxEmail[]; hasMore: boolean; nextCursor: string | null };
      })
      .then((data) => {
        if (cancelled) return;
        setEmails(data.emails);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load the inbox");
        setEmails([]);
        setHasMore(false);
        setNextCursor(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, cursor, kind, reloadToken]);

  const pageNumber = cursors.length + 1;

  function selectKind(next: InboxKind) {
    if (next === kind) return;
    setKind(next);
    setCursors([]);
    setSelectedId(null);
  }

  return (
    <section className="space-y-6">
      <PageHeader
        title="Inbox"
        description={`${tab.description} Open one to read exactly what was sent.`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setCompose({})}>
              New email
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setSettingsOpen(true)}>
              Templates &amp; signature
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setReloadToken((value) => value + 1)}>
              Refresh
            </Button>
          </div>
        }
      />

      <div role="tablist" aria-label="Email direction" className="flex gap-1 border-b border-border-subtle">
        {TABS.map((entry) => {
          const selected = entry.kind === kind;
          return (
            <button
              key={entry.kind}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => selectKind(entry.kind)}
              className={`focus-ring -mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors motion-reduce:transition-none ${
                selected
                  ? "border-accent text-content-primary"
                  : "border-transparent text-content-secondary hover:text-content-primary"
              }`}
            >
              {entry.label}
            </button>
          );
        })}
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-card border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm text-status-rejected-text"
        >
          {error}
        </p>
      )}

      {emails === null && (
        <DataTableSkeleton
          headers={tableHeaders}
          cellVariants={["text", "multiline", "text", "badge"]}
          rows={8}
          loadingLabel={`Loading ${tab.label.toLowerCase()} emails`}
        />
      )}

      {emails !== null && emails.length === 0 && !error && (
        <EmptyState
          title={`No ${tab.label.toLowerCase()} emails`}
          description={
            kind === "inbound"
              ? "Resend has no received mail on this page. If this stays empty, check that the domain's MX records point at Resend inbound."
              : "Nothing has been sent through Resend on this page."
          }
        />
      )}

      {emails !== null && emails.length > 0 && (
        <div className="overflow-x-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised shadow-card">
          <table className="w-full text-sm">
            <thead className="text-content-secondary">
              <tr>
                {tableHeaders.map((header) => (
                  <th key={header} className="px-4 py-3 text-left font-medium">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {emails.map((email) => (
                <tr
                  key={email.id}
                  onClick={() => setSelectedId(email.id)}
                  className="cursor-pointer border-t border-border-subtle align-top hover:bg-surface-overlay"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-content-secondary">
                    {formatSentAt(email.createdAt)}
                  </td>
                  <td className="max-w-[16rem] break-words px-4 py-3 text-content-primary">
                    {kind === "inbound" ? email.from || "—" : formatRecipients(email.to)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedId(email.id);
                      }}
                      className="focus-ring rounded text-left font-medium text-content-primary hover:text-accent"
                    >
                      {displaySubject(email)}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {kind === "inbound" ? (
                      <span className="text-content-secondary">{email.attachmentCount || "—"}</span>
                    ) : (
                      <Badge variant={eventVariant(email.lastEvent)}>{eventLabel(email.lastEvent)}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {emails !== null && !error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-raised px-4 py-3 text-sm text-content-secondary">
          <span>Page {pageNumber}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((current) => current.slice(0, -1))}
            >
              Newer
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasMore || !nextCursor}
              onClick={() => {
                if (nextCursor) setCursors((current) => [...current, nextCursor]);
              }}
            >
              Older
            </Button>
          </div>
        </div>
      )}

      {selectedId && (
        <EmailDetailModal
          emailId={selectedId}
          kind={kind}
          onClose={() => setSelectedId(null)}
          onReply={(draft) => {
            setSelectedId(null);
            setCompose(draft);
          }}
        />
      )}
      {compose && (
        <ComposeEmailModal
          initial={compose}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            success("Email sent");
            setKind("outbound");
            setCursors([]);
            setReloadToken((value) => value + 1);
          }}
        />
      )}
      {settingsOpen && <EmailSettingsModal onClose={() => setSettingsOpen(false)} />}
    </section>
  );
}
